import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';

const STORAGE_KEY = 'myl_pet_tab_y';
const DRAG_THRESHOLD = 5;

export interface UseDraggableTabResult {
  tabY: number;
  isDragging: boolean;
  hasDragged: boolean;
  bindPointer: {
    onPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function readStoredY(containerHeight: number): number {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw !== null) {
      const parsed = parseInt(raw, 10);
      if (!isNaN(parsed)) return clamp(parsed, 40, Math.max(41, containerHeight - 100));
    }
  } catch {
    // sessionStorage unavailable (private browsing)
  }
  return 60;
}

function useDraggableTab(containerHeight: number, onOpen: () => void): UseDraggableTabResult {
  const [tabY, setTabY] = useState(() => readStoredY(containerHeight));
  const [isDragging, setIsDragging] = useState(false);
  const [hasDragged, setHasDragged] = useState(false);

  const startY = useRef(0);
  const startTabY = useRef(0);
  const hasDraggedRef = useRef(false);
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      e.currentTarget.setPointerCapture(e.pointerId);
      startY.current = e.clientY;
      startTabY.current = tabY;
      hasDraggedRef.current = false;
      setHasDragged(false);
      setIsDragging(true);
    },
    [tabY],
  );

  useEffect(() => {
    if (!isDragging) return;
    const maxY = Math.max(41, containerHeight - 100);

    const handleMove = (e: PointerEvent) => {
      const delta = e.clientY - startY.current;
      if (Math.abs(delta) > DRAG_THRESHOLD) {
        hasDraggedRef.current = true;
        setHasDragged(true);
      }
      setTabY(clamp(startTabY.current + delta, 40, maxY));
    };

    const handleUp = () => {
      setIsDragging(false);
      if (hasDraggedRef.current) {
        setTabY((prev) => {
          try {
            sessionStorage.setItem(STORAGE_KEY, String(prev));
          } catch {}
          return prev;
        });
      } else {
        onOpenRef.current();
      }
    };

    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', handleUp);
    return () => {
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', handleUp);
    };
  }, [isDragging, containerHeight]);

  useEffect(() => {
    const maxY = Math.max(41, containerHeight - 100);
    setTabY((prev) => clamp(prev, 40, maxY));
  }, [containerHeight]);

  return { tabY, isDragging, hasDragged, bindPointer: { onPointerDown } };
}

export default useDraggableTab;
